import {
  TaskTrackerSummaryResponse,
  TrackerTaskTypeSummary,
} from '../../network/httpClients/RepositoryClient/api.schema';

/**
 * Only task types with production writers are shown; the tracker may hold
 * rows for other types (POC seeds, partial rollouts), and surfacing a type
 * before its writer ships would show stale or misleading state. Extend as
 * writers land (cross_repo_discovery next).
 */
const VISIBLE_TASK_TYPES = ['pr_knowledge_indexing', 'rules_import'];

/** Display order for visible types. */
const TASK_TYPE_ORDER = VISIBLE_TASK_TYPES;

export const ROW_TONES = ['running', 'queued', 'done', 'failed'] as const;

export type RowTone = (typeof ROW_TONES)[number];

export type TaskRow = {
  taskType: string;
  tone: RowTone;
  /** Ordered non-zero segments, e.g. [['running', 3], ['done', 12]]. */
  segments: Array<[segment: string, count: number]>;
  /** Honest completed/total repo ratio, 0..100. */
  progress: number;
  /** The counts behind the ratio — each tracked subject is a repo. */
  repos: { done: number; total: number };
  /** PRs indexed out of total, when the writer reported them. */
  prsIndexed?: { indexed: number; total: number };
  /** Rules imported across the workspace, when the writer reported them. */
  rulesImported?: number;
};

const count = (summary: TrackerTaskTypeSummary, status: string) =>
  summary.counts[status] ?? 0;

const totalCount = (summary: TrackerTaskTypeSummary) =>
  Object.values(summary.counts).reduce((sum, n) => sum + n, 0);

const activeCount = (summary: TrackerTaskTypeSummary) =>
  count(summary, 'pending') + count(summary, 'in_progress');

const failedCount = (summary: TrackerTaskTypeSummary) =>
  count(summary, 'failed') + count(summary, 'timed_out');

/** Whether any VISIBLE task type still has pending or in-progress work. */
export const summaryHasActiveTasks = (
  data: TaskTrackerSummaryResponse | undefined,
): boolean =>
  !!data?.summaries.some(
    (summary) =>
      VISIBLE_TASK_TYPES.includes(summary.taskType) && activeCount(summary) > 0,
  );

const toTone = (summary: TrackerTaskTypeSummary): RowTone => {
  if (count(summary, 'in_progress') > 0) return 'running';

  if (count(summary, 'pending') > 0) return 'queued';

  if (failedCount(summary) > 0) return 'failed';

  return 'done';
};

const toRow = (summary: TrackerTaskTypeSummary): TaskRow => {
  const total = totalCount(summary);
  const segments: TaskRow['segments'] = (
    [
      ['running', count(summary, 'in_progress')],
      ['queued', count(summary, 'pending')],
      ['done', count(summary, 'completed')],
      ['failed', count(summary, 'failed')],
      ['timed-out', count(summary, 'timed_out')],
    ] as TaskRow['segments']
  ).filter(([, n]) => n > 0);

  // The platform camelizes writer metric keys at its service boundary.
  const indexed = summary.metricTotals?.prsFullyIndexed;
  const totalPrs = summary.metricTotals?.totalPrs;
  const prsIndexed =
    summary.taskType === 'pr_knowledge_indexing' &&
    typeof indexed === 'number' &&
    typeof totalPrs === 'number' &&
    totalPrs > 0
      ? { indexed, total: totalPrs }
      : undefined;

  // No total to divide by — the writer reports what each repo produced, and a
  // repo with no rules to import legitimately contributes 0.
  const rulesCreated = summary.metricTotals?.rulesCreated;
  const rulesImported =
    summary.taskType === 'rules_import' && typeof rulesCreated === 'number'
      ? rulesCreated
      : undefined;

  return {
    taskType: summary.taskType,
    tone: toTone(summary),
    segments,
    progress:
      total > 0 ? Math.round((count(summary, 'completed') / total) * 100) : 0,
    repos: { done: count(summary, 'completed'), total },
    prsIndexed,
    rulesImported,
  };
};

export type WidgetModel = {
  rows: TaskRow[];
  /** Individual tasks (tracker rows) currently pending or in progress. */
  runningTasks: number;
  /** Individual tasks whose latest status is failed or timed out. */
  failedTasks: number;
  anythingTracked: boolean;
};

/**
 * Maps the raw tasks-summary response to the widget's display model: ordered
 * per-type rows (tone, non-zero status segments, honest completed/total
 * progress, PR-indexing totals) plus the header counts. Task types with no
 * rows at all are dropped.
 */
export const toWidgetModel = (
  data: TaskTrackerSummaryResponse | undefined,
): WidgetModel => {
  const tracked = (data?.summaries ?? []).filter(
    (s) => VISIBLE_TASK_TYPES.includes(s.taskType) && totalCount(s) > 0,
  );
  const rows = [...tracked]
    .sort((a, b) => {
      const ai = TASK_TYPE_ORDER.indexOf(a.taskType);
      const bi = TASK_TYPE_ORDER.indexOf(b.taskType);

      return (
        (ai === -1 ? TASK_TYPE_ORDER.length : ai) -
        (bi === -1 ? TASK_TYPE_ORDER.length : bi)
      );
    })
    .map(toRow);

  return {
    rows,
    runningTasks: tracked.reduce((sum, s) => sum + activeCount(s), 0),
    failedTasks: tracked.reduce((sum, s) => sum + failedCount(s), 0),
    anythingTracked: tracked.length > 0,
  };
};
